import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Tag } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  image: string;
  date: string;
  readTime: string;
  slug: string;
  category: string;
  sections?: string[];
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: "The Importance of Regular Car Maintenance in Dubai's Climate",
    content: "Dubai's scorching heat and dusty roads can take a serious toll on your vehicle. Regular maintenance isn't just a suggestion—it's a necessity. From engine overheating to battery drainage and worn-out tires, neglecting your car in this climate can lead to major repair costs. At SollBridge, we understand the challenges your vehicle faces. That's why our service packages are designed to keep your car running smoothly year-round. We offer regular oil changes, AC inspections, coolant checks, and more—ensuring your safety and prolonging your vehicle's life.",
    image: "https://images.unsplash.com/photo-1632823471565-1ecdf5c900cb?auto=format&fit=crop&q=80",
    date: "2024-02-25",
    readTime: "5 min read",
    slug: "importance-of-regular-car-maintenance",
    category: "Maintenance",
    sections: [
      "Dubai's extreme temperatures and sandy conditions create unique challenges for vehicle maintenance. The intense heat can accelerate wear and tear on various components, while fine desert sand can infiltrate crucial systems. This combination can lead to premature aging of your vehicle's components if not properly maintained.",
      "Regular oil changes, tire pressure checks, and battery inspections are crucial in Dubai's climate. Air conditioning systems require special attention, as they work harder in the intense heat. Our comprehensive maintenance approach includes regular inspections of all critical systems to ensure they can withstand the demanding conditions.",
      "Professional maintenance services ensure your vehicle receives comprehensive care from experienced technicians who understand the local climate's unique challenges. Our team is trained to identify early warning signs of heat-related stress on your vehicle's components and take preventive action before minor issues become major problems."
    ]
  },
  {
    id: '2',
    title: "Top 5 Signs Your Car Needs Immediate Servicing",
    content: "Ignoring warning signs can turn minor issues into costly repairs. Here are five red flags to watch for: Unusual noises (squealing brakes or knocking from the engine), Dashboard warning lights, Leaking fluids under the vehicle, Reduced fuel efficiency, and Vibration or pulling while driving. If you notice any of these signs, book a service with SollBridge immediately. Our technicians can diagnose and fix the issue before it gets worse.",
    image: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80",
    date: "2024-02-24",
    readTime: "4 min read",
    slug: "top-5-signs-your-car-needs-servicing",
    category: "Diagnostics",
    sections: [
      "Unusual noises from your vehicle are often the first indication of a developing problem. Squealing brakes or knocking sounds from the engine are clear indicators that your car needs professional attention. These noises often signal worn brake pads or engine problems that should be addressed immediately to prevent more serious damage.",
      "Modern vehicles are equipped with sophisticated diagnostic systems that monitor various components. When a warning light appears on your dashboard, it's your car's way of telling you something needs attention. Different warning lights indicate various issues, from minor maintenance requirements to serious mechanical problems that require immediate attention.",
      "Fluid leaks are another critical warning sign that should never be ignored. Spots or puddles under your parked car could indicate serious problems with your vehicle's systems. Different colored fluids can help identify the source - from engine oil to transmission fluid, brake fluid, or coolant. Any fluid leak should be inspected by a professional to prevent potential system failure and ensure your safety on the road."
    ]
  },
  {
    id: '3',
    title: "How to Choose the Right Car Service Package for Your Vehicle",
    content: "Choosing the right service package can save you money and prevent breakdowns. For daily drivers, a basic maintenance package every 5,000 km may suffice. For luxury cars or vehicles over 5 years old, a premium or full-service package is ideal. At SollBridge, we offer tailored packages for all car types—from Japanese to German makes. Not sure what you need? Our experts can assess your car's condition and recommend the perfect fit.",
    image: "https://images.unsplash.com/photo-1530046339160-ce3e530c7d2f?auto=format&fit=crop&q=80",
    date: "2024-02-23",
    readTime: "6 min read",
    slug: "choosing-right-service-package",
    category: "Service Packages",
    sections: [
      "Basic maintenance packages are perfect for daily drivers and newer vehicles. These packages include essential services like oil changes, filter replacements, and basic inspections. Regular basic maintenance helps prevent common issues and ensures your vehicle runs efficiently. Our basic packages are designed to provide comprehensive care while remaining cost-effective.",
      "Premium service packages are ideal for luxury vehicles and cars over 5 years old. These comprehensive packages include detailed inspections and preventive maintenance that go beyond basic servicing. Premium packages often include advanced diagnostic tests, comprehensive fluid checks, and thorough inspections of critical systems. This level of service helps maintain your vehicle's performance and value while preventing potential issues before they become serious problems."
    ]
  },
  {
    id: '4',
    title: "The Benefits of Professional Fluid Top-Ups",
    content: "Fluids are the lifeblood of your vehicle. Engine oil, brake fluid, coolant, transmission fluid, and windshield washer fluid all play a key role in your car's performance. Topping up fluids without proper knowledge can do more harm than good. At SollBridge, we use manufacturer-recommended fluids and ensure every system is checked thoroughly. Regular fluid top-ups enhance performance, prevent overheating, and extend the life of critical components.",
    image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80",
    date: "2024-02-22",
    readTime: "4 min read",
    slug: "benefits-of-professional-fluid-maintenance",
    category: "Maintenance",
    sections: [
      "Each fluid in your vehicle serves a specific purpose and requires careful maintenance. Engine oil lubricates moving parts and prevents wear, brake fluid enables safe stopping, coolant regulates temperature, transmission fluid ensures smooth gear changes, and windshield washer fluid maintains visibility. Understanding the role of each fluid is crucial for proper vehicle maintenance.",
      "While some fluid checks can be done at home, professional service ensures the right type and amount of fluid is used. Our technicians use manufacturer-recommended fluids and follow precise specifications for each vehicle make and model. This attention to detail prevents common issues like fluid contamination or overfilling, which can cause serious damage to your vehicle's systems."
    ]
  },
  {
    id: '5',
    title: "Why Timely Oil Changes Are Crucial for Engine Health",
    content: "Oil changes are often overlooked—but they're one of the most important things you can do for your engine. Fresh oil reduces friction, keeps your engine clean, and prevents wear. In Dubai's harsh climate, oil can degrade faster than usual, making timely changes even more essential. At SollBridge, we provide fast, affordable oil change services using premium oils suitable for all car brands.",
    image: "https://images.unsplash.com/photo-1635764749881-b158d8e92a8f?auto=format&fit=crop&q=80",
    date: "2024-02-21",
    readTime: "4 min read",
    slug: "importance-of-timely-oil-changes",
    category: "Maintenance",
    sections: [
      "Regular oil changes are crucial for maintaining engine health and performance. Engine oil serves multiple purposes: it lubricates moving parts, helps cool the engine, and carries away contaminants. Over time, oil breaks down and becomes less effective at performing these vital functions.",
      "In Dubai's extreme climate, oil degradation occurs more rapidly than in moderate climates. High temperatures accelerate oil breakdown, while dust and sand can increase contamination. This makes regular oil changes even more critical for vehicles operating in the UAE.",
      "Using the right type of oil is just as important as changing it regularly. Modern engines require specific oil grades and specifications. Our technicians use only manufacturer-approved oils and follow precise specifications for each vehicle make and model."
    ]
  },
  {
    id: '6',
    title: "Understanding Brake System Maintenance",
    content: "Brakes are your car's most important safety system. Regular brake maintenance ensures quicker stops and better control. Warning signs of brake issues include squealing sounds, a soft pedal, and longer stopping distances. SollBridge technicians inspect brake pads, rotors, and brake fluid with every service. Whether it's a simple pad replacement or a full system overhaul, we've got you covered.",
    image: "https://images.unsplash.com/photo-1486006920555-c77dcf18193c?auto=format&fit=crop&q=80",
    date: "2024-02-20",
    readTime: "5 min read",
    slug: "brake-system-maintenance-guide",
    category: "Safety",
    sections: [
      "Your vehicle's brake system is a complex network of components working together to ensure safe stopping. Regular maintenance of these components is crucial for optimal performance and safety. Understanding the basics of brake system maintenance can help you recognize when service is needed.",
      "Common brake problems often start with warning signs that shouldn't be ignored. Squealing or grinding noises, vibration in the brake pedal, or increased stopping distance are all indicators that your brakes need attention. Addressing these issues promptly can prevent more expensive repairs later.",
      "Professional brake service includes inspection of all major components: brake pads, rotors, calipers, and brake fluid. Our technicians use specialized equipment to measure component wear and test brake system performance, ensuring your vehicle meets safety standards."
    ]
  },
  {
    id: '7',
    title: "Preparing Your Car for the Summer Heat in Dubai",
    content: "Dubai summers can exceed 45°C, and your car needs to be ready. Make sure your coolant is full, AC is functioning properly, tires are in good condition, and your battery is tested. At SollBridge, we offer a special summer prep package to help your car beat the heat. Don't wait until it's too late—book your service before the heatwave hits.",
    image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80",
    date: "2024-02-19",
    readTime: "6 min read",
    slug: "summer-car-preparation-guide",
    category: "Seasonal Care",
    sections: [
      "Summer in Dubai presents unique challenges for vehicles. High temperatures can cause coolant to evaporate faster, put extra strain on your air conditioning system, and accelerate battery wear. Regular maintenance becomes even more critical during these months to prevent breakdowns and ensure comfort.",
      "Our summer preparation service includes a comprehensive check of all heat-sensitive components. We inspect and test your cooling system, air conditioning, battery health, and tire condition. This thorough approach helps identify potential issues before they become problems in the peak of summer.",
      "Prevention is always better than cure, especially when it comes to summer car care. Our technicians will ensure your vehicle is properly prepared for the extreme temperatures, helping you avoid costly repairs and uncomfortable breakdowns in the heat."
    ]
  },
  {
    id: '8',
    title: "The Advantages of Choosing SollBridge for Your Car Care Needs",
    content: "Why do hundreds of Dubai residents trust SollBridge? Because we combine quality, convenience, and affordability. Our certified mechanics use top-grade tools and parts, and our transparent pricing means no hidden fees. Plus, our online booking system makes scheduling easy. We treat every car like it's our own, ensuring customer satisfaction every step of the way.",
    image: "https://images.unsplash.com/photo-1625047509168-a7026f36de04?auto=format&fit=crop&q=80",
    date: "2024-02-18",
    readTime: "5 min read",
    slug: "why-choose-sollbridge",
    category: "Company",
    sections: [
      "At SollBridge, we pride ourselves on our commitment to excellence. Our team of certified technicians undergoes regular training to stay up-to-date with the latest automotive technologies and service techniques. This ensures your vehicle receives the best possible care using current industry standards.",
      "We understand that convenience is crucial for our customers. That's why we offer flexible scheduling, online booking, and transparent communication throughout the service process. Our digital service reports keep you informed about your vehicle's condition and any recommended maintenance.",
      "Quality and affordability shouldn't be mutually exclusive. We use only high-quality parts and materials while maintaining competitive pricing. Our transparent pricing policy means you'll never face unexpected charges, and we'll always explain any recommended services before proceeding."
    ]
  },
  {
    id: '9',
    title: "Common Myths About Car Maintenance Debunked",
    content: "You don't need to change your oil until 10,000 km. False. In Dubai's climate, you may need changes sooner. Premium fuel makes your car faster. Not true—unless your manufacturer recommends it. At SollBridge, we believe in educating our customers. Trust the experts, not myths. Book a check-up and let us walk you through what your car really needs.",
    image: "https://images.unsplash.com/photo-1493238792000-8113da705763?auto=format&fit=crop&q=80",
    date: "2024-02-17",
    readTime: "4 min read",
    slug: "car-maintenance-myths-debunked",
    category: "Education",
    sections: [
      "Many car maintenance myths can lead to costly mistakes. Regular maintenance schedules are designed based on scientific testing and real-world experience. Following manufacturer recommendations, especially in Dubai's climate, is crucial for maintaining your vehicle's performance and longevity.",
      "Another common myth is that all engine oils are the same. Different engines require specific oil grades and types. Using the wrong oil can lead to reduced performance and potential engine damage. Our technicians always use the manufacturer-recommended oil grade for your specific vehicle.",
      "Some believe that maintenance is only necessary when problems arise. This reactive approach often leads to more expensive repairs and shorter vehicle lifespan. Regular preventive maintenance helps identify and address issues before they become major problems."
    ]
  },
  {
    id: '10',
    title: "A Step-by-Step Guide to SollBridge's Service Process",
    content: "First, visit our website to choose a service and book an appointment. Next, drop off your vehicle or use our pickup option. Our technicians will inspect your vehicle and begin servicing. Once completed, we'll notify you and share a report. Payment can be made online or at pickup. It's quick, reliable, and hassle-free—just the way car care should be. Experience the SollBridge difference today.",
    image: "https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?auto=format&fit=crop&q=80",
    date: "2024-02-16",
    readTime: "3 min read",
    slug: "sollbridge-service-process",
    category: "Service Guide",
    sections: [
      "Our service process begins with a thorough initial inspection. This helps us identify any immediate concerns and ensure we address all your vehicle's needs. We document our findings and provide you with a detailed report and recommendations.",
      "During servicing, our technicians follow manufacturer-specified procedures and use specialized tools to ensure quality work. We keep you updated on the progress and any additional issues discovered during the service. This transparent communication helps you make informed decisions about your vehicle's maintenance.",
      "After service completion, we perform a final quality check to ensure everything meets our high standards. We provide you with a comprehensive service report detailing all work performed and any recommendations for future maintenance. This documentation helps you track your vehicle's service history and plan for future maintenance needs."
    ]
  }
];

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Blog post not found
          </h1>
          <Link
            to="/blog"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Return to blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareText = `Check out this article: ${post.title}`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Hero Section */}
      <div className="relative bg-cover bg-center h-[400px]"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("${post.image}")`
        }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl">
            <Link 
              to="/blog"
              className="flex items-center justify-center mb-8 text-white hover:text-blue-300 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Blog
            </Link>
            <div className="flex items-center justify-center space-x-4 mb-4">
              <span className="px-3 py-1 bg-blue-600 bg-opacity-50 rounded-full text-sm">
                {post.category}
              </span>
              <div className="flex items-center text-sm">
                <Clock className="w-4 h-4 mr-2" />
                <span>{post.readTime}</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {post.title}
            </h1>
            <div className="text-sm opacity-75">
              {new Date(post.date).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Share buttons */}
        <div className="flex items-center justify-center space-x-4 mb-12">
          <span className="text-gray-600 dark:text-gray-400 flex items-center">
            <Share2 className="w-4 h-4 mr-2" />
            Share this article:
          </span>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700"
          >
            <Twitter className="w-5 h-5" />
          </a>
          <a
            href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${encodeURIComponent(post.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>

        <article className="prose prose-lg dark:prose-invert max-w-none">
          {/* Main content */}
          <div className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-12">
            {post.content}
          </div>

          {/* Featured image */}
          <img
            src={post.image}
            alt={post.title}
            className="w-full rounded-lg mb-12"
          />

          {/* Article sections */}
          <div className="space-y-12">
            {post.sections?.map((section, index) => (
              <div key={index} className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {section}
              </div>
            ))}
          </div>
        </article>
      </div>
    </div>
  );
}

export { BlogPostPage }